import { Link, useParams } from "react-router-dom";
import { useIsFetching, useQueryClient } from "@tanstack/react-query";
import { QueueLayout } from "../../features/web3/forex/components/QueueLayout";
import { Td } from "../../features/web3/forex/components/table";
import { formatDateTime } from "../../lib/format";
import { useForexAdmin } from "./api";
import { Tab } from "./AccountDetail";
import { Blocked } from "./ForexShell";
import { dailyRange } from "./logic";
import { BASE, ListView, Money, QueryState, Stat, TabStrip, linkCls, mono, muted, useTab } from "./parts";

// Deleted accounts (028): gone from the trading server, read from the mirror by
// MT5 login alone. The app no longer knows them, so no owner and no id.

const COLUMNS = [
  { label: "Login" },
  { label: "Group" },
  { label: "First activity" },
  { label: "Last activity" },
  { label: "Last day end balance", align: "right" },
];

const row = (r) => (
  <tr key={r.login} className="hover:bg-white/[0.02]">
    <Td className={mono}>
      <Link to={`${BASE}/deleted-accounts/${r.login}`} className={linkCls}>{r.login}</Link>
    </Td>
    <Td className={muted}>{r.group}</Td>
    <Td className={muted}>{formatDateTime(r.firstActivityAt)}</Td>
    <Td className={muted}>{formatDateTime(r.lastActivityAt)}</Td>
    <Td align="right"><Money value={r.balance} /></Td>
  </tr>
);

export default function DeletedAccounts() {
  const qc = useQueryClient();
  const fetching = useIsFetching({ queryKey: ["forex-admin"] }) > 0;
  return (
    <QueueLayout
      eyebrow="Forex Admin"
      title="Deleted"
      accent="accounts"
      description="Client accounts deleted from the trading server whose history the backup still holds, newest last activity first. Activity is roughly when it opened and stopped, not when it was deleted. The balance is its last day end, not live."
      isFetching={fetching}
      onRefresh={() => qc.invalidateQueries({ queryKey: ["forex-admin"] })}
    >
      <ListView
        path="/admin/deleted-accounts"
        empty={{ login: "", group: "", from: "", to: "" }}
        fields={[
          { name: "login", label: "MT5 login" },
          { name: "group", label: "Group (whole, e.g. real\\hedging\\dev-pcx\\ecn)" },
          { name: "from", label: "Last activity from", type: "datetime-local" },
          { name: "to", label: "Last activity to", type: "datetime-local" },
        ]}
        columns={COLUMNS}
        row={row}
        label="accounts"
        emptyText="No deleted accounts match."
      />
    </QueueLayout>
  );
}

const TABS = [
  { value: "trades", label: "Closed trades" },
  { value: "deals", label: "Deals" },
  { value: "order-history", label: "Order history" },
  { value: "daily", label: "Daily" },
];

export function DeletedAccountDetail() {
  const { login } = useParams();
  const [asked, setTab] = useTab("trades");
  // A live account's tab in the URL (positions, pnl, ...) has no deleted route.
  const tab = TABS.some((t) => t.value === asked) ? asked : "trades";
  // The history routes do not answer the row's fields, so the list gives them.
  const list = useForexAdmin("/admin/deleted-accounts", { login });
  const qc = useQueryClient();
  const fetching = useIsFetching({ queryKey: ["forex-admin"] }) > 0;
  const a = list.data?.entries[0];

  return (
    <QueueLayout
      eyebrow="Forex Admin · Deleted account"
      title={login}
      accent="deleted"
      description={a ? a.group : null}
      isFetching={fetching}
      onRefresh={() => qc.invalidateQueries({ queryKey: ["forex-admin"] })}
    >
      <Link to={`${BASE}/deleted-accounts`} className={`text-xs text-muted-foreground ${linkCls}`}>
        ← All deleted accounts
      </Link>

      <QueryState q={list}>
        {(d) =>
          d.total === 0 ? (
            // One answer for every reason (028): never say the login is live.
            <Blocked title="No such deleted account">
              This login is not one of this environment's deleted accounts.{" "}
              <Link to={`${BASE}/accounts?login=${login}`} className={linkCls}>Search the live accounts</Link>.
            </Blocked>
          ) : (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
                <Stat label="Login">{a.login}</Stat>
                <Stat label="Group"><span className="break-all text-sm">{a.group}</span></Stat>
                <Stat label="First activity"><span className="text-sm">{formatDateTime(a.firstActivityAt)}</span></Stat>
                <Stat label="Last activity"><span className="text-sm">{formatDateTime(a.lastActivityAt)}</span></Stat>
                <Stat label="Last day end balance"><Money value={a.balance} /></Stat>
              </div>
              <div className="space-y-5">
                <TabStrip tabs={TABS} value={tab} onChange={setTab} />
                <Tab
                  key={tab}
                  tab={tab}
                  base={`/admin/deleted-accounts/${login}`}
                  daily={dailyRange(a.firstActivityAt, a.lastActivityAt)}
                />
              </div>
            </div>
          )
        }
      </QueryState>
    </QueueLayout>
  );
}
